import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Pencil,
  Highlighter,
  Eraser,
  ChevronDown,
  Undo2,
  Redo2,
  MousePointer2,
  Hand,
  BookOpen,
  FileText,
  StickyNote,
  Type,
  Sigma,
  BarChart3,
  MoveUpRight,
  Trash2,
  Shapes,
  Square,
  Circle,
  Minus,
  ArrowRight,
  Triangle
} from 'lucide-react';
import { ToolType, ShapeType, COLORS } from './types';

const ToolButton = ({ active, onClick, icon, label, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`wb-tool-button ${active ? 'active' : ''}`}
    title={label}
  >
    {icon}
    <span className="wb-tool-tooltip">{label}</span>
  </button>
);

const PopoverPortal = ({ anchorRef, className, children }) => {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const narrow = window.matchMedia?.('(max-width: 1100px)').matches;
      setStyle(narrow
        ? {
          position: 'fixed',
          left: Math.max(8, Math.min(rect.left, window.innerWidth - 220)),
          bottom: Math.max(8, window.innerHeight - rect.top + 8),
        }
        : {
          position: 'fixed',
          left: rect.right + 8,
          top: rect.top,
        });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef]);

  if (!style) return null;
  return createPortal(
    <div className={className} style={style}>
      {children}
    </div>,
    document.body
  );
};

const ShapeSelector = ({ currentShapeType, setShapeType, onClose }) => (
  <>
    <button
      type="button"
      onClick={() => { setShapeType(ShapeType.RECTANGLE); onClose(); }}
      className={`wb-shape-option ${currentShapeType === ShapeType.RECTANGLE ? 'active' : ''}`}
      title="Rectangle"
    >
      <Square size={18} />
    </button>
    <button
      type="button"
      onClick={() => { setShapeType(ShapeType.CIRCLE); onClose(); }}
      className={`wb-shape-option ${currentShapeType === ShapeType.CIRCLE ? 'active' : ''}`}
      title="Circle"
    >
      <Circle size={18} />
    </button>
    <button
      type="button"
      onClick={() => { setShapeType(ShapeType.LINE); onClose(); }}
      className={`wb-shape-option ${currentShapeType === ShapeType.LINE ? 'active' : ''}`}
      title="Line"
    >
      <Minus size={18} />
    </button>
    <button
      type="button"
      onClick={() => { setShapeType(ShapeType.ARROW); onClose(); }}
      className={`wb-shape-option ${currentShapeType === ShapeType.ARROW ? 'active' : ''}`}
      title="Arrow"
    >
      <ArrowRight size={18} />
    </button>
    <button
      type="button"
      onClick={() => { setShapeType(ShapeType.TRIANGLE); onClose(); }}
      className={`wb-shape-option ${currentShapeType === ShapeType.TRIANGLE ? 'active' : ''}`}
      title="Triangle"
    >
      <Triangle size={18} />
    </button>
  </>
);

const EraserSelector = ({ eraserSize, setEraserSize, eraserMode, setEraserMode }) => (
  <>
    <div className="wb-eraser-selector__label">Size</div>
    <input
      type="range"
      min={12}
      max={96}
      step={2}
      value={eraserSize}
      className="wb-eraser-selector__slider"
      onChange={(e) => setEraserSize(Number(e.target.value))}
    />
    <div className="wb-eraser-selector__value">{eraserSize}px</div>
    <div className="wb-eraser-selector__label">Mode</div>
    <div className="wb-eraser-selector__modes">
      <button
        type="button"
        className={`wb-eraser-selector__mode ${eraserMode === 'normal' ? 'active' : ''}`}
        onClick={() => setEraserMode('normal')}
      >
        Normal
      </button>
      <button
        type="button"
        className={`wb-eraser-selector__mode ${eraserMode === 'stroke' ? 'active' : ''}`}
        onClick={() => setEraserMode('stroke')}
      >
        By Stroke
      </button>
    </div>
  </>
);

const Toolbar = ({
  currentTool,
  currentColor,
  currentShapeType,
  eraserSize,
  eraserMode,
  setTool,
  setColor,
  setShapeType,
  setEraserSize,
  setEraserMode,
  onAddNote,
  onAddText,
  onAddMath,
  onAddChart,
  onAddFbd,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onClear,
  boardMode,
  onModeChange
}) => {
  const [showShapeSelector, setShowShapeSelector] = useState(false);
  const [showEraserSelector, setShowEraserSelector] = useState(false);
  const shapeAnchorRef = useRef(null);
  const eraserAnchorRef = useRef(null);

  useEffect(() => {
    if (currentTool !== ToolType.ERASER) {
      setShowEraserSelector(false);
    }
  }, [currentTool]);

  const handleShapeToolClick = () => {
    if (currentTool === ToolType.SHAPE) {
      setShowShapeSelector(!showShapeSelector);
    } else {
      setTool(ToolType.SHAPE);
      setShowShapeSelector(true);
      setShowEraserSelector(false);
    }
  };

  const handleEraserToolClick = () => {
    setTool(ToolType.ERASER);
    setShowShapeSelector(false);
  };

  const handleEraserOptionsToggle = () => {
    if (currentTool !== ToolType.ERASER) {
      setTool(ToolType.ERASER);
      setShowShapeSelector(false);
    }
    setShowEraserSelector((prev) => !prev);
  };

  return (
    <div className="wb-toolbar">
      <div className="wb-toolbar-section">
        <ToolButton
          active={boardMode !== 'notebook'}
          onClick={() => onModeChange?.('whiteboard')}
          icon={<FileText size={20} />}
          label="Whiteboard"
        />
        <ToolButton
          active={boardMode === 'notebook'}
          onClick={() => onModeChange?.('notebook')}
          icon={<BookOpen size={20} />}
          label="Notebook"
        />
      </div>

      {/* Drawing Tools */}
      <div className="wb-toolbar-section">
        <ToolButton
          active={currentTool === ToolType.PAN}
          onClick={() => { setTool(ToolType.PAN); setShowShapeSelector(false); setShowEraserSelector(false); }}
          icon={<Hand size={20} />}
          label="Pan (Space)"
        />
        <ToolButton
          active={currentTool === ToolType.SELECT}
          onClick={() => { setTool(ToolType.SELECT); setShowShapeSelector(false); setShowEraserSelector(false); }}
          icon={<MousePointer2 size={20} />}
          label="Select (V)"
        />
        <ToolButton
          active={currentTool === ToolType.TEXT}
          onClick={() => { setTool(ToolType.TEXT); setShowShapeSelector(false); setShowEraserSelector(false); }}
          icon={<Type size={20} />}
          label="Click to Type (T)"
        />
        <ToolButton
          active={currentTool === ToolType.PEN}
          onClick={() => { setTool(ToolType.PEN); setShowShapeSelector(false); setShowEraserSelector(false); }}
          icon={<Pencil size={20} />}
          label="Pen (P)"
        />
        <ToolButton
          active={currentTool === ToolType.MARKER}
          onClick={() => { setTool(ToolType.MARKER); setShowShapeSelector(false); setShowEraserSelector(false); }}
          icon={<Highlighter size={20} />}
          label="Marker (H)"
        />
        <div className="wb-eraser-tool-wrapper" ref={eraserAnchorRef}>
          <ToolButton
            active={currentTool === ToolType.ERASER}
            onClick={handleEraserToolClick}
            icon={<Eraser size={20} />}
            label="Eraser (E)"
          />
          <button
            type="button"
            className={`wb-eraser-options-toggle ${showEraserSelector ? 'active' : ''}`}
            onClick={handleEraserOptionsToggle}
            onPointerDown={(event) => event.stopPropagation()}
            title="Eraser options"
            aria-label="Toggle eraser options"
            aria-expanded={showEraserSelector}
          >
            <ChevronDown size={12} />
          </button>
          {showEraserSelector && currentTool === ToolType.ERASER && (
            <PopoverPortal anchorRef={eraserAnchorRef} className="wb-eraser-selector">
              <EraserSelector
                eraserSize={eraserSize}
                setEraserSize={setEraserSize}
                eraserMode={eraserMode}
                setEraserMode={setEraserMode}
              />
            </PopoverPortal>
          )}
        </div>
        <div className="wb-shape-tool-wrapper" ref={shapeAnchorRef}>
          <ToolButton
            active={currentTool === ToolType.SHAPE}
            onClick={handleShapeToolClick}
            icon={<Shapes size={20} />}
            label="Shape (S)"
          />
          {showShapeSelector && currentTool === ToolType.SHAPE && (
            <PopoverPortal anchorRef={shapeAnchorRef} className="wb-shape-selector">
              <ShapeSelector
                currentShapeType={currentShapeType}
                setShapeType={setShapeType}
                onClose={() => setShowShapeSelector(false)}
              />
            </PopoverPortal>
          )}
        </div>
      </div>

      {/* Colors - show when pen, marker, or shape is active */}
      {(currentTool === ToolType.PEN || currentTool === ToolType.MARKER || currentTool === ToolType.SHAPE) && (
        <div className="wb-toolbar-section wb-color-section">
          {COLORS.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setColor(c)}
              className={`wb-color-swatch ${currentColor === c ? 'active' : ''}`}
              style={{ backgroundColor: c }}
              title={`Color ${c}`}
            />
          ))}
        </div>
      )}

      {/* Object Tools */}
      <div className="wb-toolbar-section">
        <ToolButton
          onClick={() => onAddNote()}
          icon={<StickyNote size={20} />}
          label="Add Note (N)"
        />
        <ToolButton
          onClick={() => onAddText?.()}
          icon={<FileText size={20} />}
          label="Add Text"
        />
        <ToolButton
          onClick={() => onAddMath()}
          icon={<Sigma size={20} />}
          label="Add Math (M)"
        />
        <ToolButton
          onClick={() => onAddChart()}
          icon={<BarChart3 size={20} />}
          label="Add Graph (G)"
        />
        <ToolButton
          onClick={() => onAddFbd?.()}
          icon={<MoveUpRight size={20} />}
          label="Add FBD"
        />
      </div>

      {/* Actions */}
      <div className="wb-toolbar-section">
        <ToolButton
          onClick={onUndo}
          icon={<Undo2 size={20} />}
          label="Undo (Ctrl/Cmd+Z)"
          disabled={!canUndo}
        />
        <ToolButton
          onClick={onRedo}
          icon={<Redo2 size={20} />}
          label="Redo (Ctrl/Cmd+Y)"
          disabled={!canRedo}
        />
        <button
          type="button"
          onClick={onClear}
          className="wb-tool-button wb-clear-button"
          title="Clear Board"
        >
          <Trash2 size={20} />
          <span className="wb-tool-tooltip">Clear Board</span>
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
